'use client'

import { Disclosure, DisclosureButton, DisclosurePanel, useClose } from '@headlessui/react'
import { ChevronRightIcon } from '@heroicons/react/20/solid'
import { useLocalStorageState } from 'ahooks'
import _ from 'lodash'
import NextLink from 'next/link'
import { twMerge } from 'tailwind-merge'
import { menu } from '@/settings'
import type { Menu } from '@/types'

export default function Navigation({ pathname }: { pathname: string }) {
  return (
    <nav className='flex flex-1 flex-col'>
      <ul className='-mx-2 space-y-1'>
        {menu.map((item) => {
          return (
            <li key={item.name}>
              <MenuItem item={item} pathname={pathname} />
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

const defaultStyle = 'pr-2 py-1.5 lg:py-1 gap-x-2 font-normal flex items-center w-full rounded'
const style = 'hover:bg-zinc-100 dark:hover:bg-zinc-700 md:text-sm'
const styleCurrent = 'bg-zinc-800 dark:bg-zinc-400 text-white dark:text-black'
const styleGroupName = 'font-medium dark:font-normal text-uppercase tracking-wide mt-2'

function MenuItem({ item, pathname }: { item: Menu; pathname: string }) {
  const close = useClose()
  const key = `admin-nav-${_.camelCase(item.name)}`
  const [navOpen, setNavOpen] = useLocalStorageState<boolean>(key)

  if (!item.children) {
    return (
      <NextLink
        href={item.href}
        onClick={close}
        className={twMerge(item.icon ? 'pl-3' : 'pl-8', defaultStyle, styleGroupName, item.href === pathname ? styleCurrent : style)}
      >
        {item.icon && <span className='shrink-0 opacity-70'>{item.icon}</span>}
        {item.name}
      </NextLink>
    )
  }

  return (
    <Disclosure as='div' defaultOpen={Boolean(navOpen)} suppressHydrationWarning>
      <DisclosureButton
        className={twMerge(item.href === pathname ? styleCurrent : style, defaultStyle, styleGroupName, 'group gap-x-3')}
        onClick={() => setNavOpen(!navOpen)}
      >
        <ChevronRightIcon
          aria-hidden='true'
          className='h-5 w-5 shrink-0 text-zinc-400 group-data-open:rotate-90 group-data-open:text-zinc-500'
        />
        {item.name}
      </DisclosureButton>
      <DisclosurePanel as='ul' className='mt-1 mx-2 list-none px-2 space-y-1'>
        {item.children.map((subItem) => (
          <li key={subItem.name}>
            <NextLink
              href={subItem.href}
              onClick={close}
              className={twMerge(
                'flex items-center w-full rounded text-left md:text-sm',
                defaultStyle,
                subItem.href === pathname ? styleCurrent : style,
                'ml-4 pl-4',
              )}
            >
              {subItem.name}
            </NextLink>
          </li>
        ))}
      </DisclosurePanel>
    </Disclosure>
  )
}
